// redux/slices/clientVisitsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { resetStore } from "./resetStore"
import { getServerUrl } from "../../Utils/serverSelector"
import { getJwtToken } from "../../Utils/authStorage"

const axiosRequest = async ({ url, method = "GET", extraHeaders = {}, data = null }) => {
  const jwt = await getJwtToken()                        // ← свежий токен
  const response = await axios({
    url: `${getServerUrl()}${url}`,
    method,
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...extraHeaders,
    },
    data,
  })
  return response.data
}

// ===== THUNKS =====

// Все посещения мастера
export const fetchMasterVisits = createAsyncThunk(
  "clientVisits/fetchMasterVisits",
  async ({ uid, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      if (filters.clientId) params.append("clientId", filters.clientId)
      if (filters.status) params.append("status", filters.status)
      if (filters.startDate) params.append("startDate", filters.startDate)
      if (filters.endDate) params.append("endDate", filters.endDate)

      const url = `/client-visits/${uid}${params.toString() ? `?${params.toString()}` : ""}`
      const response = await axiosRequest({ url })
      return response
    } catch (error) {
      console.error("Fetch Master Visits Error:", error.message)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Посещения конкретного клиента
export const fetchClientVisits = createAsyncThunk(
  "clientVisits/fetchClientVisits",
  async ({ clientId }, { rejectWithValue }) => {
    try {
      const response = await axiosRequest({ url: `/client-visits/client/${clientId}` })
      return response
    } catch (error) {
      console.error("Fetch Client Visits Error:", error.message)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Создать посещение
export const createVisit = createAsyncThunk(
  "clientVisits/createVisit",
  async (visitData, { rejectWithValue }) => {
    try {
      const response = await axiosRequest({
        url: "/client-visits",
        method: "POST",
        data: visitData,
      })

      // бэкенд может вернуть либо { visit }, либо сам объект
      const visit = response.visit || response
      return visit
    } catch (error) {
      console.error("Create Visit Error:", error.message)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Обновить посещение
export const updateVisit = createAsyncThunk(
  "clientVisits/updateVisit",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosRequest({
        url: `/client-visits/${id}`,
        method: "PUT",
        data,
      })
      return response.visit || response
    } catch (error) {
      console.error("Update Visit Error:", error.message)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Удалить посещение
export const deleteVisit = createAsyncThunk(
  "clientVisits/deleteVisit",
  async ({ id }, { rejectWithValue }) => {
    try {
      await axiosRequest({
        url: `/client-visits/${id}`,
        method: "DELETE",
      })
      return id
    } catch (error) {
      console.error("Delete Visit Error:", error.message)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Статистика клиента
export const fetchClientStats = createAsyncThunk(
  "clientVisits/fetchClientStats",
  async ({ clientId }, { rejectWithValue }) => {
    try {
      const response = await axiosRequest({
        url: `/client-visits/client/${clientId}/stats`,
      })
      return { clientId, stats: response }
    } catch (error) {
      console.error("Fetch Client Stats Error:", error.message)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

const initialState = {
  visits: [],
  clientVisits: {},  // { [clientId]: Visit[] }
  clientStats: {},   // { [clientId]: stats }
  status: "idle",
  error: null,
}

const clientVisitsSlice = createSlice({
  name: "clientVisits",
  initialState,
  reducers: {
    clearVisits: (state) => {
      state.visits = []
      state.clientVisits = {}
    },
    clearError: (state) => {
      state.error = null
    },
    clearClientStats: (state) => {
      state.clientStats = {}
    },
  },
  extraReducers: (builder) => {
    builder
      // Master visits
      .addCase(fetchMasterVisits.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchMasterVisits.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.visits = action.payload || []
      })
      .addCase(fetchMasterVisits.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || action.error?.message
      })

      // Client visits
      .addCase(fetchClientVisits.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchClientVisits.fulfilled, (state, action) => {
        state.status = "succeeded"
        const clientId = action.meta.arg.clientId
        state.clientVisits[clientId] = action.payload || []
      })
      .addCase(fetchClientVisits.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || action.error?.message
      })

      // Create visit
      .addCase(createVisit.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(createVisit.fulfilled, (state, action) => {
        state.status = "succeeded"
        const visit = action.payload
        if (!visit) return

        const clientId = visit.clientId
        if (!clientId) return

        // общий список
        state.visits.unshift(visit)

        // история конкретного клиента
        if (!state.clientVisits[clientId]) {
          state.clientVisits[clientId] = []
        }
        state.clientVisits[clientId].unshift(visit)
      })
      .addCase(createVisit.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || action.error?.message
      })

      // Update visit
      .addCase(updateVisit.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(updateVisit.fulfilled, (state, action) => {
        state.status = "succeeded"
        const updated = action.payload
        if (!updated) return

        // общий список
        const idx = state.visits.findIndex((v) => v.id === updated.id)
        if (idx !== -1) state.visits[idx] = updated

        const clientId = updated.clientId
        if (clientId && Array.isArray(state.clientVisits[clientId])) {
          const cIdx = state.clientVisits[clientId].findIndex(
            (v) => v.id === updated.id,
          )
          if (cIdx !== -1) state.clientVisits[clientId][cIdx] = updated
        }
      })
      .addCase(updateVisit.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || action.error?.message
      })

      // Delete visit
      .addCase(deleteVisit.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(deleteVisit.fulfilled, (state, action) => {
        state.status = "succeeded"
        const id = action.payload
        state.visits = state.visits.filter((v) => v.id !== id)

        Object.keys(state.clientVisits).forEach((clientId) => {
          state.clientVisits[clientId] =
            (state.clientVisits[clientId] || []).filter((v) => v.id !== id)
        })
      })
      .addCase(deleteVisit.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || action.error?.message
      })

      // Client stats
      .addCase(fetchClientStats.fulfilled, (state, action) => {
        const { clientId, stats } = action.payload || {}
        if (clientId) {
          state.clientStats[clientId] = stats
        }
      })

      // resetStore
      .addCase(resetStore, () => initialState)
  },
})

export const { clearVisits, clearError, clearClientStats } = clientVisitsSlice.actions
export default clientVisitsSlice.reducer
